import React from 'react'
import PropTypes from 'prop-types'
import { default as dayjs } from 'dayjs'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Y from 'yup'

import { useGetMutateCall } from '../../lib/api/get'
import { useCreateCall } from '../../lib/api/create'
import { useUpdateCall } from '../../lib/api/update'
import { useDeleteCall } from '../../lib/api/delete'
import { createYupSchema } from '../../lib/createYUPSchema'
import { EditMode } from './EditMode'
import invalid from '../../assets/images/invalid.svg'

export const ActivityModal = ({
  isLocalEnvironment,
  authDetails,
  setOpenActivity,
  modSchema,
  defaultData,
  roomVal,
  autoRefresh,
  dates,
  setSelectedApplData,
}) => {
  React.useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setOpenActivity(false)
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [])
  const getCallMutation = useGetMutateCall()
  const createCallMutation = useCreateCall()
  const updateCallMutation = useUpdateCall()
  const deleteCallMutation = useDeleteCall()
  const [activeTab, setActiveTab] = React.useState(0)
  const [enableEdit, setEnableEdit] = React.useState(false)
  const [editData, setEditData] = React.useState(null)
  const [deleteData, setDeleteData] = React.useState(null)
  const [deleteConfirm, setDeleteConfirm] = React.useState(false)

  const validations = [
    {
      name: 'activityDetailLabel',
      validationType: 'string',
      validations: [{ type: 'required', params: ['Label is required!'] }],
    },
  ]
  const validation = Y.object().shape({
    ...validations.reduce(createYupSchema, {}),
  })

  const validateAdd = Y.object().shape({
    ...modSchema.rowForm.create.buildValidation.reduce(createYupSchema, {}),
  })

  const validateUpdate = Y.object().shape({
    ...modSchema.rowForm.update.buildValidation.reduce(createYupSchema, {}),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validation),
  })

  const {
    register: register2,
    handleSubmit: handleSubmit2,
    formState: { errors: errors2 },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validateAdd),
  })

  const {
    register: register3,
    handleSubmit: handleSubmit3,
    formState: { errors: errors3 },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validateUpdate),
  })

  const onCreate = async (data) => {
    data['activityId'] = defaultData[modSchema.rowForm.rowDataArray][0].activityId
    data['lastUpdatedByUserId'] = authDetails.session.userId

    await createCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/addData',
        apiEndpoint: 'activityDetail',
        data: data,
        messageTitle: 'Activities',
        message: 'Activity Uploaded!',
        isMultipart: true,
        isDynamic: true,
        params: '',
      })
      .then(async () => {
        await getCallMutation
          .mutateAsync({
            apiURL: isLocalEnvironment + '/getData',
            apiEndpoint: 'attendance',
            params:
              '&checkInDateTime=' +
              dayjs.utc(dates === null ? new Date() : dates).format('YYYY-MM-DD HH:mm:ss') +
              `&classRoomId=${roomVal}`,
            doPolling: autoRefresh,
            isDynamic: true,
          })
          .then((resp) => {
            setSelectedApplData(resp.formData)
          })
          .catch(() => {})
        setActiveTab(0)
      })
  }

  const onUpdate = async (data) => {
    data['activityId'] = defaultData[modSchema.rowForm.rowDataArray][0].activityId
    data['lastUpdatedByUserId'] = authDetails.session.userId

    await updateCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/updateData',
        apiEndpoint: 'activityDetail',
        data: data,
        id: editData.activityDetailId,
        messageTitle: 'Activities',
        message: 'Activity Updated!',
        isMultipart: true,
        isDynamic: true,
        params: '',
      })
      .then(async () => {
        await getCallMutation
          .mutateAsync({
            apiURL: isLocalEnvironment + '/getData',
            apiEndpoint: 'attendance',
            params:
              '&checkInDateTime=' +
              dayjs.utc(dates === null ? new Date() : dates).format('YYYY-MM-DD HH:mm:ss') +
              `&classRoomId=${roomVal}`,
            doPolling: autoRefresh,
            isDynamic: true,
          })
          .then((resp) => {
            setSelectedApplData(resp.formData)
          })
          .catch(() => {})
        setActiveTab(0)
      })
  }

  const onUpload = async (data) => {
    let formData = new FormData()
    formData.append('activityDetailType', 'image')
    formData.append('file', data.activityDetailDescription[0])
    formData.append('activityDetailLabel', data.activityDetailLabel)
    formData.append('activityDetailNotes', 'Image')

    await createCallMutation
      .mutateAsync({
        url: `${isLocalEnvironment}/upload?endPoint=activityDetail/image&activityId=${defaultData.activities[0].activityId}`,
        apiEndpoint: modSchema.rowForm.update.formAPI,
        parentAPI: modSchema.rowForm.update.parentAPI,
        data: formData,
        headers: {
          tokenSource: 'passport',
          Authorization: `Bearer ${authDetails.authentication.tokenObject}`,
        },
        messageTitle: 'Activities',
        message: 'Activity Uploaded!',
        isMultipart: true,
        isDynamic: true,
        params: '',
      })
      .then(async () => {
        await getCallMutation
          .mutateAsync({
            apiURL: isLocalEnvironment + '/getData',
            apiEndpoint: 'attendance',
            params:
              '&checkInDateTime=' +
              dayjs.utc(dates === null ? new Date() : dates).format('YYYY-MM-DD HH:mm:ss') +
              `&classRoomId=${roomVal}`,
            doPolling: autoRefresh,
            isDynamic: true,
          })
          .then((resp) => {
            setSelectedApplData(resp.formData)
          })
          .catch(() => {})
        setActiveTab(0)
      })
  }

  const getDoc = async (id) => {
    await getCallMutation
      .mutateAsync({
        apiURL: isLocalEnvironment + '/getData',
        apiEndpoint: 'activityDetail',
        id: id,
        params: '',
        isDynamic: true,
      })
      .then((resp) => {
        const link = document.createElement('a')
        link.setAttribute('href', resp.formData?.fileURL)
        link.setAttribute('target', '_blank')

        document.body.appendChild(link)

        link.click()
      })
  }

  const closeAdd = () => {
    setActiveTab(0)
  }

  const openDelete = (data) => {
    setDeleteConfirm(true)
    setDeleteData(data)
  }

  const closeDelete = () => {
    setDeleteConfirm(false)
    setDeleteData(null)
  }

  const openEdit = (data) => {
    setEnableEdit(true)
    setEditData(data)
  }

  const closeEdit = () => {
    setEditData({})
    setEnableEdit(false)
  }

  const onDelete = async () => {
    await deleteCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/deleteData',
        apiEndpoint: modSchema.rowForm.update.formAPI,
        parentAPI: modSchema.rowForm.update.parentAPI,
        id: deleteData[modSchema.rowForm.update.id],
        messageTitle: modSchema.rowForm.update.deleteMessage.title,
        message: modSchema.rowForm.update.deleteMessage.message,
        isDynamic: true,
        params: '',
      })
      .then(() => closeDelete())
  }

  const proForm = [
    {
      name: 'Activities',
    },
    {
      name: 'Add Activity',
    },
    {
      name: 'Upload File',
    },
  ]

  return (
    <div
      className="trtui-modal trtui-modal-xl trtui-fade trtui-show"
      aria-labelledby="exampleModalLabel"
      style={{ display: 'block', backgroundColor: 'rgba(55,55,55,0.3)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="trtui-modal-dialog trtui-modal-dialog-scrollable trtui-modal-xl">
        <div className="trtui-modal-content">
          <div className="trtui-modal-header trtui-bg-light trtui-p-3">
            <h5 className="trtui-modal-title" id="exampleModalLabel">
              Activity Details
            </h5>
            <button
              type="button"
              className="trtui-btn-close"
              onClick={() => {
                setOpenActivity(false)
              }}
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="trtui-modal-body">
            <div className="trtui-card">
              <div className="trtui-card-header">
                <ul
                  className="trtui-nav trtui-nav-tabs-custom trtui-rounded trtui-card-header-tabs trtui-border-bottom-0"
                  role="tablist"
                >
                  {!enableEdit &&
                    proForm.length > 0 &&
                    proForm.map((form, formIndex) => (
                      <li className="trtui-nav-item" role="presentation" key={formIndex}>
                        <span
                          className={`trtui-nav-link trtui-cursor-pointer ${
                            activeTab === formIndex ? 'trtui-active' : ''
                          }`}
                          data-bs-toggle="tab"
                          role="tab"
                          onClick={() => setActiveTab(formIndex)}
                        >
                          {form.name}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="trtui-card-body trtui-p-4">
                {enableEdit ? (
                  <EditMode
                    key={`${enableEdit}${editData}${closeEdit}${editData.activityDetailId}`}
                    handleSubmit3={handleSubmit3}
                    updateCallMutation={updateCallMutation}
                    closeEdit={closeEdit}
                    editData={editData}
                    register3={register3}
                    errors3={errors3}
                    onUpdate={onUpdate}
                    setOpenActivity={setOpenActivity}
                  />
                ) : deleteConfirm ? (
                  <div>
                    <div className="trtui-mt-2 trtui-text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/gsqxdxog.json"
                        trigger="loop"
                        colors="primary:#f7b84b,secondary:#f06548"
                        style={{ width: '100px', height: '100px' }}
                      />
                      <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                        <h4>Are you Sure ?</h4>
                        <p className="trtui-text-muted trtui-mx-4 trtui-mb-0">
                          Are you Sure You want to delete this?
                        </p>
                      </div>
                    </div>
                    <div className="trtui-d-flex trtui-gap-2 trtui-justify-content-center trtui-mt-4 trtui-mb-2">
                      <button
                        type="button"
                        className="trtui-btn trtui-w-sm trtui-btn-light"
                        disabled={deleteCallMutation.isLoading}
                        data-bs-dismiss="modal"
                        onClick={() => {
                          closeDelete()
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="trtui-btn trtui-w-sm trtui-btn-danger"
                        disabled={deleteCallMutation.isLoading}
                        onClick={() => onDelete()}
                      >
                        {deleteCallMutation.isLoading ? (
                          <span className="trtui-d-flex trtui-align-items-center">
                            <span className="trtui-flex-grow-1 trtui-me-2">Deleting...</span>
                            <span
                              className="trtui-spinner-border trtui-flex-shrink-0"
                              role="status"
                            >
                              <span className="trtui-visually-hidden">Deleting...</span>
                            </span>
                          </span>
                        ) : (
                          'Yes, Delete It!'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="trtui-tab-content">
                    <div
                      className={`trtui-tab-pane ${activeTab === 0 ? 'trtui-active' : ''}`}
                      role="tabpanel"
                    >
                      <div
                        className="trtui-table-responsive trtui-mt-xl-0"
                        style={{ overflowX: 'scroll', transform: 'rotateX(180deg)' }}
                      >
                        <table
                          className="trtui-table trtui-table-white trtui-table-striped trtui-table-nowrap trtui-align-middle trtui-mb-0"
                          style={{ transform: 'rotateX(180deg)' }}
                        >
                          <thead>
                            <tr>
                              {modSchema.rowForm.view.tableHeaders.map((h, hInd) => (
                                <th scope="col" key={hInd}>
                                  {h}
                                </th>
                              ))}
                              {modSchema.rowForm.view.noActions !== true && (
                                <th scope="col">Action</th>
                              )}
                            </tr>
                          </thead>
                          {modSchema.rowForm.filterBy && modSchema.rowForm.filterByValue ? (
                            <tbody>
                              {defaultData.length > 0 &&
                                defaultData
                                  .filter(
                                    (f) =>
                                      f[modSchema.rowForm.filterBy] ===
                                      modSchema.rowForm.filterByValue,
                                  )
                                  .map((c, cInd) => (
                                    <tr key={cInd}>
                                      {modSchema.rowForm.view.tableRows.map((r, rInd) => (
                                        <td key={rInd}>{c[r]}</td>
                                      ))}
                                      {modSchema.rowForm.view.noActions !== true && (
                                        <td>
                                          <div className="trtui-hstack trtui-gap-3 trtui-flex-wrap">
                                            <span
                                              onClick={() => openEdit(c)}
                                              className="trtui-link-success trtui-fs-15 trtui-cursor-pointer"
                                            >
                                              <i className="ri-edit-2-line" />
                                            </span>
                                            <span
                                              onClick={() => openDelete(c)}
                                              className="trtui-link-danger trtui-fs-15 trtui-cursor-pointer"
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </span>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                            </tbody>
                          ) : (
                            <tbody>
                              {defaultData[modSchema.rowForm.rowDataArray][0].activityDetails
                                .length > 0 &&
                                defaultData[modSchema.rowForm.rowDataArray][0].activityDetails
                                  .sort(
                                    (a, b) =>
                                      a.activityDetailSortOrderNumber -
                                      b.activityDetailSortOrderNumber,
                                  )
                                  .map((c, cInd) =>
                                    c.activityDetailType === 'image' ? (
                                      <tr key={cInd}>
                                        <td>{c.activityDetailLabel}</td>
                                        <td colSpan={modSchema.rowForm.view.tableRows.length - 1}>
                                          Download file.
                                        </td>
                                        <td>
                                          <div className="trtui-hstack trtui-gap-3 trtui-flex-wrap">
                                            <span
                                              className="trtui-link-primary trtui-fs-15 trtui-cursor-pointer"
                                              onClick={() => getDoc(c.activityDetailId)}
                                              disabled={getCallMutation.isLoading}
                                            >
                                              {getCallMutation.isLoading ? (
                                                <span
                                                  className="trtui-spinner-border trtui-spinner-border-sm"
                                                  role="status"
                                                  aria-hidden="true"
                                                ></span>
                                              ) : (
                                                <i className="ri-download-2-line" />
                                              )}
                                            </span>
                                            <span
                                              onClick={() => openDelete(c)}
                                              className="trtui-link-danger trtui-fs-15 trtui-cursor-pointer"
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : (
                                      <tr key={cInd}>
                                        {modSchema.rowForm.view.tableRows.map((r, rInd) => (
                                          <td key={rInd}>{c[r]}</td>
                                        ))}
                                        <td>
                                          <div className="trtui-hstack trtui-gap-3 trtui-flex-wrap">
                                            <span
                                              onClick={() => openEdit(c)}
                                              className="trtui-link-success trtui-fs-15 trtui-cursor-pointer"
                                            >
                                              <i className="ri-edit-2-line" />
                                            </span>
                                            <span
                                              onClick={() => openDelete(c)}
                                              className="trtui-link-danger trtui-fs-15 trtui-cursor-pointer"
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    ),
                                  )}
                            </tbody>
                          )}
                        </table>
                      </div>
                    </div>
                    <div
                      className={`trtui-tab-pane ${activeTab === 1 ? 'trtui-active' : ''}`}
                      role="tabpanel"
                    >
                      <form
                        onSubmit={handleSubmit2(onCreate)}
                        id="addActivity"
                        data-testid="formAdd"
                      >
                        <div className="trtui-row trtui-g-2">
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-control-label">Type</label>
                            <div className="trtui-input-group trtui-has-validation">
                              <input
                                type="text"
                                placeholder="Label"
                                className={`trtui-form-control ${errors2['activityDetailType'] && 'trtui-is-invalid'}`}
                                style={{
                                  color: '#000000',
                                  paddingRight: 'calc(1.5em + 1rem)',
                                  backgroundImage:
                                    errors['activityDetailType'] && `url("${invalid}")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                                }}
                                {...register2('activityDetailType')}
                              />
                              {errors2['activityDetailType'] && (
                                <div className="trtui-invalid-feedback">
                                  {errors2['activityDetailType'].message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-control-label">Label</label>
                            <div className="trtui-input-group trtui-has-validation">
                              <input
                                type="text"
                                placeholder="Label"
                                className={`trtui-form-control ${errors2['activityDetailLabel'] && 'trtui-is-invalid'}`}
                                {...register2('activityDetailLabel')}
                              />
                              {errors2['activityDetailLabel'] && (
                                <div className="trtui-invalid-feedback">
                                  {errors2['activityDetailLabel'].message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-label">Description</label>
                            <textarea
                              type="text"
                              placeholder="Description"
                              {...register2('activityDetailDescription')}
                              className={`trtui-form-control ${errors2['activityDetailDescription'] && 'trtui-is-invalid'}`}
                            ></textarea>
                            {errors2['activityDetailDescription'] && (
                              <div className="trtui-invalid-feedback">
                                {errors2['activityDetailDescription'].message}
                              </div>
                            )}
                          </div>
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-label">Notes</label>
                            <textarea
                              type="text"
                              placeholder="Notes"
                              {...register2('activityDetailNotes')}
                              className={`trtui-form-control ${errors2['activityDetailNotes'] && 'trtui-is-invalid'}`}
                              style={{
                                color: '#000000',
                                paddingRight: 'calc(1.5em + 1rem)',
                                backgroundImage:
                                  errors2['activityDetailNotes'] && `url("${invalid}")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                                backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                              }}
                            ></textarea>
                            {errors2['activityDetailNotes'] && (
                              <div className="trtui-invalid-feedback">
                                {errors2['activityDetailNotes'].message}
                              </div>
                            )}
                          </div>
                          <div className="trtui-col-12">
                            <button
                              className={`trtui-btn trtui-btn-primary trtui-btn-sm`}
                              type="submit"
                              disabled={createCallMutation.isLoading}
                            >
                              {createCallMutation.isLoading ? (
                                <span className="trtui-d-flex trtui-align-items-center">
                                  <span className="trtui-flex-grow-1 trtui-me-2">Adding...</span>
                                  <span
                                    className="trtui-spinner-border trtui-flex-shrink-0"
                                    role="status"
                                  >
                                    <span className="trtui-visually-hidden">Adding...</span>
                                  </span>
                                </span>
                              ) : (
                                'Add'
                              )}
                            </button>{' '}
                            <button
                              className={`trtui-btn trtui-btn-light trtui-btn-sm`}
                              type="button"
                              disabled={createCallMutation.isLoading}
                              onClick={closeAdd}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                    <div
                      className={`trtui-tab-pane ${activeTab === 2 ? 'trtui-active' : ''}`}
                      role="tabpanel"
                    >
                      <form onSubmit={handleSubmit(onUpload)} data-testid="formAdd">
                        <div className="trtui-row trtui-g-2">
                          <div
                            className={`trtui-col-md-6 trtui-has-validation`}
                            style={{ display: 'none' }}
                          >
                            <label className="trtui-form-control-label">Image</label>
                            <div className="trtui-input-group trtui-has-validation">
                              <input
                                type="text"
                                className={`trtui-form-control ${errors['activityDetailType'] && 'trtui-is-invalid'}`}
                                style={{
                                  color: '#000000',
                                  paddingRight: 'calc(1.5em + 1rem)',
                                  backgroundImage:
                                    errors['activityDetailType'] && `url("${invalid}")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                                }}
                                {...register('activityDetailType')}
                              />
                              {errors['activityDetailType'] && (
                                <div className="trtui-invalid-feedback">
                                  {errors['activityDetailType'].message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-control-label">Description</label>
                            <div className="trtui-input-group trtui-has-validation">
                              <input
                                type="text"
                                placeholder="Description"
                                className={`trtui-form-control ${errors['activityDetailLabel'] && 'trtui-is-invalid'}`}
                                style={{
                                  color: '#000000',
                                  paddingRight: 'calc(1.5em + 1rem)',
                                  backgroundImage:
                                    errors['activityDetailLabel'] && `url("${invalid}")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                                }}
                                {...register('activityDetailLabel')}
                              />
                              {errors['activityDetailLabel'] && (
                                <div className="trtui-invalid-feedback">
                                  {errors['activityDetailLabel'].message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={`trtui-col-md-6 trtui-has-validation`}>
                            <label className="trtui-form-control-label">Image</label>
                            <div className="trtui-input-group trtui-has-validation">
                              <input
                                type="file"
                                className={`trtui-form-control ${errors['activityDetailDescription'] && 'trtui-is-invalid'}`}
                                style={{
                                  color: '#000000',
                                  paddingRight: 'calc(1.5em + 1rem)',
                                  backgroundImage:
                                    errors['activityDetailDescription'] && `url("${invalid}")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                                  backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                                }}
                                {...register('activityDetailDescription')}
                              />
                              {errors['activityDetailDescription'] && (
                                <div className="trtui-invalid-feedback">
                                  {errors['activityDetailDescription'].message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="trtui-col-12">
                            <button
                              className={`trtui-btn trtui-btn-primary trtui-btn-sm`}
                              type="submit"
                              disabled={createCallMutation.isLoading}
                            >
                              {createCallMutation.isLoading ? (
                                <span className="trtui-d-flex trtui-align-items-center">
                                  <span className="trtui-flex-grow-1 trtui-me-2">Uploading...</span>
                                  <span
                                    className="trtui-spinner-border trtui-flex-shrink-0"
                                    role="status"
                                  >
                                    <span className="trtui-visually-hidden">Uploading...</span>
                                  </span>
                                </span>
                              ) : (
                                'Upload'
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ActivityModal.propTypes = {
  isLocalEnvironment: PropTypes.string,
  authDetails: PropTypes.object,
  childFormData: PropTypes.object,
  setOpenActivity: PropTypes.func,
  modSchema: PropTypes.object,
  defaultData: PropTypes.object,
  roomVal: PropTypes.string,
  autoRefresh: PropTypes.bool,
  dates: PropTypes.string,
  setSelectedApplData: PropTypes.func,
}
